/* Multi-Size Quantity Matrix
 *
 * Lets a customer enter a quantity per size on the product page and add all
 * sizes to the cart in a single Add to Cart action. Stays in sync with the
 * theme's existing variant pickers (e.g. Color) — when a non-size option
 * changes, each size cell rebinds to the variant matching that combination.
 */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('multi-size-matrix')) {
    return;
  }

  // Shopify-compatible client-side money formatter.
  // Mirrors Shopify's standard formatMoney implementation so output matches
  // server-rendered prices for any shop.money_format pattern.
  function formatMoneyShopify(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = typeof thousands === 'undefined' ? ',' : thousands;
      decimal = typeof decimal === 'undefined' ? '.' : decimal;
      precision = typeof precision === 'undefined' ? 2 : precision;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const decPart = parts[1] ? decimal + parts[1] : '';
      return intPart + decPart;
    }

    const placeholder = formatString.match(placeholderRegex);
    if (!placeholder) return formatString;
    let value = '';
    switch (placeholder[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  class MultiSizeMatrix extends HTMLElement {
    connectedCallback() {
      this.unique = this.dataset.unique || '';
      this.formId = this.dataset.formId || '';
      this.sizePosition = parseInt(this.dataset.sizePosition, 10) || 0;
      this.minOrderQty = Math.max(0, parseInt(this.dataset.minOrderQty, 10) || 0);

      const dataEl = this.querySelector('[data-matrix-variants]');
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (err) {
        console.error('[multi-size-matrix] variant JSON parse failed', err);
        return;
      }

      this.form = document.getElementById(this.formId);
      if (!this.form) {
        console.warn('[multi-size-matrix] form not found:', this.formId);
        return;
      }

      this.cells = Array.from(this.querySelectorAll('[data-size-cell]'));
      this.inputs = Array.from(this.querySelectorAll('[data-size-qty]'));
      this.errorEl = this.querySelector('[data-matrix-error]');
      this.addBtn = this.form.querySelector('[data-add-to-cart]');
      this.addBtnText = this.form.querySelector('[data-add-to-cart-text]');
      this.addBtnPriceEl = this.form.querySelector('[data-add-to-cart] [data-product-price]');
      this.addBtnComparePriceEl = this.form.querySelector('[data-add-to-cart] [data-compare-price]');
      this.summaryEl = document.querySelector('[data-matrix-summary][data-unique="' + this.unique + '"]')
        || document.querySelector('[data-matrix-summary]');
      this.summaryEachEl = this.summaryEl && this.summaryEl.querySelector('[data-summary-each]');
      this.summaryQtyEl = this.summaryEl && this.summaryEl.querySelector('[data-summary-qty]');
      this.summaryUnitEl = this.summaryEl && this.summaryEl.querySelector('[data-summary-unit]');
      this.summaryTotalEl = this.summaryEl && this.summaryEl.querySelector('[data-summary-total]');
      this.summaryTotalBigEl = this.summaryEl && this.summaryEl.querySelector('[data-summary-total-big]');
      this.unitLabel = this.summaryEl ? (this.summaryEl.dataset.unitLabel || 'items') : 'items';

      // Header total (shown next to "2. Choose Sizes")
      this.matrixTotalQtyEl = this.querySelector('[data-matrix-total-qty]');
      this.matrixTotalUnitEl = this.querySelector('[data-matrix-total-unit]');

      // Progress bar (volume discount tiers)
      this.progressEl = this.querySelector('[data-msm-progress]');
      this.progressFillEl = this.querySelector('[data-msm-fill]');
      this.progressStatusEl = this.querySelector('[data-msm-status]');
      this.progressCartInfoEl = this.querySelector('[data-msm-cart-info]');
      this.progressBadgeEl = this.querySelector('[data-msm-badge]');
      this.progressCongratsEl = this.querySelector('[data-msm-congrats]');
      this.confettiHostEl = this.querySelector('[data-msm-confetti]');
      this.wasMaxed = false;
      this.progressMarkerEls = Array.from(this.querySelectorAll('[data-msm-marker]'));
      this.progressLegendAmountEls = Array.from(
        this.querySelectorAll('[data-msm-legend-amount]')
      );
      this.tiers = this.parseTiers();
      this.cartTotalCents = 0;
      this.cartItemCount = 0;

      // Multi-currency: detect the customer's display currency + conversion rate.
      // window.Shopify.currency.rate is set when the storefront supports
      // multiple presentment currencies. cart.currency (from /cart.js) gives
      // the active ISO code so we can format amounts in the right currency.
      this.cartCurrencyCode =
        (window.theme && window.theme.current_iso_code) || null;
      this.shopCurrencyRate = 1;
      try {
        if (
          window.Shopify &&
          window.Shopify.currency &&
          window.Shopify.currency.rate
        ) {
          const r = parseFloat(window.Shopify.currency.rate);
          if (!isNaN(r) && r > 0) this.shopCurrencyRate = r;
        }
      } catch (_e) {}

      this.refreshFromOptions();
      this.updateTotals();
      this.updateAddState();
      this.fetchCartTotal();

      // Keep the progress bar in sync when the cart changes elsewhere
      // (cart drawer add/remove, quantity bump, cross-tab updates).
      this._cartRefreshHandler = () => this.fetchCartTotal();
      ['cart:refresh', 'cart:update', 'cart:updated', 'cart:item:added'].forEach(
        (evt) => document.addEventListener(evt, this._cartRefreshHandler)
      );
      this._visibilityHandler = () => {
        if (document.visibilityState === 'visible') this.fetchCartTotal();
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);

      this.inputs.forEach((inp) => {
        inp.addEventListener('input', () => this.onQtyChange(inp));
        inp.addEventListener('change', () => this.onQtyChange(inp));
        inp.addEventListener('focus', () => {
          if (inp.value === '0') inp.select();
        });
      });

      // +/- step buttons inside each cell
      this.querySelectorAll('[data-size-qty-step]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const cell = btn.closest('[data-size-cell]');
          if (!cell) return;
          const input = cell.querySelector('[data-size-qty]');
          if (!input || input.disabled) return;
          const step = parseInt(btn.dataset.sizeQtyStep, 10) || 0;
          const current = parseInt(input.value, 10) || 0;
          const next = Math.max(0, current + step);
          input.value = String(next);
          this.onQtyChange(input);
        });
      });

      // Watch for non-size option changes (e.g. Color) anywhere in the page.
      // Theme uses radio inputs and hidden [data-single-option-selector] inputs.
      this._optionChangeHandler = (e) => this.onOptionChange(e);
      document.addEventListener('change', this._optionChangeHandler, true);

      // Intercept form submit (capture phase = before theme's product-form handler).
      this._submitHandler = (e) => this.onSubmit(e);
      this.form.addEventListener('submit', this._submitHandler, true);

      // Defensive: also catch Add to Cart click in case theme bypasses submit.
      if (this.addBtn) {
        this._clickHandler = (e) => {
          if (this.addBtn.disabled || this.totalQty() === 0) {
            e.preventDefault();
            e.stopPropagation();
          }
        };
        this.addBtn.addEventListener('click', this._clickHandler, true);
      }
    }

    disconnectedCallback() {
      if (this._optionChangeHandler) {
        document.removeEventListener('change', this._optionChangeHandler, true);
      }
      if (this._submitHandler && this.form) {
        this.form.removeEventListener('submit', this._submitHandler, true);
      }
      if (this._clickHandler && this.addBtn) {
        this.addBtn.removeEventListener('click', this._clickHandler, true);
      }
      if (this._cartRefreshHandler) {
        ['cart:refresh', 'cart:update', 'cart:updated', 'cart:item:added'].forEach(
          (evt) => document.removeEventListener(evt, this._cartRefreshHandler)
        );
      }
      if (this._visibilityHandler) {
        document.removeEventListener('visibilitychange', this._visibilityHandler);
      }
    }

    onOptionChange(e) {
      const target = e.target;
      if (!target || !target.closest) return;
      const inSelects = target.closest('variant-selects, [data-product-options]');
      if (!inSelects) return;

      // Figure out which option position changed; ignore size changes — we own size.
      const position = this.readOptionPosition(target);
      if (position && position === this.sizePosition) return;

      // Defer so the theme finishes its own variant resolution first.
      setTimeout(() => this.refreshFromOptions(), 30);
    }

    readOptionPosition(el) {
      const name = el.getAttribute('name') || '';
      let m = name.match(/option(\d+)/);
      if (m) return parseInt(m[1], 10);
      const idx = (el.dataset && el.dataset.index) || '';
      m = idx.match(/option(\d+)/);
      if (m) return parseInt(m[1], 10);
      return 0;
    }

    getSelectedNonSizeOptions() {
      const result = {};
      const scope =
        document.getElementById('variant-selects-' + this.unique) ||
        this.form ||
        document;

      scope.querySelectorAll('input[type="radio"]').forEach((radio) => {
        if (!radio.checked) return;
        const pos = this.readOptionPosition(radio);
        if (pos && pos !== this.sizePosition) result[pos] = radio.value;
      });

      scope.querySelectorAll('[data-single-option-selector]').forEach((inp) => {
        const pos = this.readOptionPosition(inp);
        if (pos && pos !== this.sizePosition && inp.value) {
          if (!(pos in result)) result[pos] = inp.value;
        }
      });

      return result;
    }

    findVariant(sizeValue, selectedOptions) {
      const sizeKey = 'option' + this.sizePosition;
      return this.data.variants.find((v) => {
        if (v[sizeKey] !== sizeValue) return false;
        for (const pos in selectedOptions) {
          if (v['option' + pos] !== selectedOptions[pos]) return false;
        }
        return true;
      });
    }

    refreshFromOptions() {
      const selected = this.getSelectedNonSizeOptions();

      this.cells.forEach((cell) => {
        const sizeValue = cell.dataset.sizeValue;
        const input = cell.querySelector('[data-size-qty]');
        const soldOutEl = cell.querySelector('[data-sold-out]');
        const priceEl = cell.querySelector('[data-size-price]');
        const variant = this.findVariant(sizeValue, selected);

        const setUnavailable = (showLabel) => {
          input.disabled = true;
          input.value = '0';
          input.dataset.variantId = '';
          input.dataset.variantPrice = '0';
          cell.classList.add('multi-size-matrix__cell--sold-out');
          if (soldOutEl) soldOutEl.hidden = !showLabel;
          if (priceEl) priceEl.textContent = '—';
        };

        if (!variant) {
          setUnavailable(true);
          return;
        }

        input.dataset.variantId = String(variant.id);
        input.dataset.variantPrice = String(variant.price || 0);
        if (priceEl) priceEl.textContent = this.formatMoney(variant.price || 0);

        if (!variant.available) {
          setUnavailable(true);
        } else {
          input.disabled = false;
          cell.classList.remove('multi-size-matrix__cell--sold-out');
          if (soldOutEl) soldOutEl.hidden = true;
        }
      });

      this.updateTotals();
      this.updateAddState();
    }

    onQtyChange(input) {
      let v = parseInt(input.value, 10);
      if (isNaN(v) || v < 0) v = 0;
      // Clamp to max if Shopify provides one via variant.inventory_quantity in future.
      input.value = String(v);
      this.hideError();
      this.updateTotals();
      this.updateAddState();
    }

    // Pick the price-per-unit to show in "X each".
    // Prefer the variant currently selected by the theme's pickers; else first
    // available variant in the matrix; else first variant.
    currentEachPrice() {
      const selected = this.getSelectedNonSizeOptions();
      // Find any size that has matching variant in current color
      for (const cell of this.cells) {
        const variant = this.findVariant(cell.dataset.sizeValue, selected);
        if (variant && variant.available) return variant.price;
      }
      if (this.data.variants && this.data.variants[0]) return this.data.variants[0].price;
      return 0;
    }

    computeTotalCents() {
      let total = 0;
      this.inputs.forEach((inp) => {
        if (inp.disabled) return;
        const qty = parseInt(inp.value, 10);
        const price = parseInt(inp.dataset.variantPrice, 10);
        if (!isNaN(qty) && qty > 0 && !isNaN(price)) total += qty * price;
      });
      return total;
    }

    updateTotals() {
      const totalCents = this.computeTotalCents();
      const totalQty = this.totalQty();
      const eachCents = this.currentEachPrice();

      if (this.summaryEachEl) {
        this.summaryEachEl.textContent = this.formatMoney(eachCents);
      }
      if (this.summaryQtyEl) {
        this.summaryQtyEl.textContent = String(totalQty);
      }
      if (this.summaryUnitEl) {
        this.summaryUnitEl.textContent = this.unitLabel;
      }
      if (this.summaryTotalEl) {
        this.summaryTotalEl.textContent = this.formatMoney(totalCents);
      }
      if (this.summaryTotalBigEl) {
        this.summaryTotalBigEl.textContent = this.formatMoney(totalCents);
      }

      // Header total ("X polos total" next to "2. Choose Sizes")
      if (this.matrixTotalQtyEl) {
        this.matrixTotalQtyEl.textContent = String(totalQty);
      }
      if (this.matrixTotalUnitEl) {
        this.matrixTotalUnitEl.textContent = this.unitLabel + ' total';
      }

      // Empty-state: hide price summary rows when nothing chosen
      const isEmpty = totalQty === 0;
      if (this.summaryEl) {
        this.summaryEl.classList.toggle('is-empty', isEmpty);
      }

      // Minimum order check
      const minQty = this.minOrderQty || 0;
      const belowMin = minQty > 0 && totalQty > 0 && totalQty < minQty;
      const remaining = belowMin ? minQty - totalQty : 0;

      // Snapshot the original ATC button content once so we can restore it.
      if (this.addBtnText && this.addBtnTextOriginal == null) {
        this.addBtnTextOriginal = this.addBtnText.innerHTML;
      }

      if (this.addBtnText) {
        if (isEmpty) {
          if (minQty > 0) {
            this.addBtnText.textContent = 'Select at least ' + minQty + ' ' + this.unitLabel + ' to add to cart';
          } else {
            this.addBtnText.textContent = 'Select size quantities to add to cart';
          }
        } else if (belowMin) {
          this.addBtnText.textContent = 'Add ' + remaining + ' more to reach the ' + minQty + '-' + this.unitLabel.replace(/s$/, '') + ' minimum';
        } else {
          // Restore the original markup (text + inner [data-product-price] span)
          if (this.addBtnTextOriginal != null && !this.addBtnText.querySelector('[data-product-price]')) {
            this.addBtnText.innerHTML = this.addBtnTextOriginal;
            this.addBtnPriceEl = this.form.querySelector('[data-add-to-cart] [data-product-price]');
            this.addBtnComparePriceEl = this.form.querySelector('[data-add-to-cart] [data-compare-price]');
          }
          if (this.addBtnPriceEl) {
            this.addBtnPriceEl.textContent = this.formatMoney(totalCents);
          }
        }
      }
      // Hide compare-at price line on ATC button while matrix is active.
      if (this.addBtnComparePriceEl) {
        this.addBtnComparePriceEl.classList.add('hidden');
      }

      this.updateProgress(totalCents);
    }

    parseTiers() {
      const el = this.querySelector('[data-msm-tiers]');
      if (!el) return [];
      try {
        const parsed = JSON.parse(el.textContent);
        return parsed
          .filter((t) => typeof t.min_cents === 'number' && t.min_cents > 0)
          .sort((a, b) => a.min_cents - b.min_cents);
      } catch (err) {
        console.warn('[multi-size-matrix] tier JSON parse failed', err);
        return [];
      }
    }

    async fetchCartTotal() {
      try {
        const res = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        const cart = await res.json();
        this.cartTotalCents = parseInt(cart.total_price, 10) || 0;
        this.cartItemCount = parseInt(cart.item_count, 10) || 0;
        if (cart.currency) this.cartCurrencyCode = cart.currency;
      } catch (err) {
        this.cartTotalCents = 0;
        this.cartItemCount = 0;
      }
      this.refreshLegendAmounts();
      this.updateCartInfo();
      this.updateProgress(this.computeTotalCents());
    }

    updateCartInfo() {
      if (!this.progressCartInfoEl) return;
      if (this.cartTotalCents > 0) {
        const noun = this.cartItemCount === 1 ? 'item' : 'items';
        this.progressCartInfoEl.innerHTML =
          'Includes <strong>' +
          this.formatCartMoney(this.cartTotalCents) +
          '</strong> already in your cart (' +
          this.cartItemCount +
          ' ' +
          noun +
          ')';
        this.progressCartInfoEl.hidden = false;
      } else {
        this.progressCartInfoEl.hidden = true;
      }
    }

    // Tier thresholds live in SHOP currency cents (matching the admin
    // discount configuration). Convert to the customer's display currency
    // cents using Shopify.currency.rate (defaults to 1 on single-currency).
    tierDisplayCents(tier) {
      return Math.round(tier.min_cents * (this.shopCurrencyRate || 1));
    }

    // Currency-aware money formatter. Prefers Intl.NumberFormat with the
    // detected cart currency (handles symbols/locale correctly across
    // Markets), falls back to the theme's shop-currency format string.
    formatCartMoney(cents) {
      if (typeof cents !== 'number' || isNaN(cents)) cents = 0;
      const code = this.cartCurrencyCode;
      if (code && typeof Intl !== 'undefined' && Intl.NumberFormat) {
        try {
          return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: code,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(cents / 100);
        } catch (_e) {}
      }
      return this.formatMoney(cents);
    }

    // Re-render the Liquid-rendered legend amounts using the detected cart
    // currency rate. Liquid `| money` already converts on Markets storefronts,
    // so this is mostly a no-op when rate === 1; but keeps amounts in sync
    // when the storefront doesn't expose presentment-converted Liquid.
    refreshLegendAmounts() {
      if (!this.progressLegendAmountEls || !this.tiers.length) return;
      // Only override if we actually detected a non-default rate AND a
      // currency code; otherwise Liquid's server-rendered amount is fine.
      if (this.shopCurrencyRate === 1 && !this.cartCurrencyCode) return;
      this.progressLegendAmountEls.forEach((el) => {
        const item = el.closest('[data-tier-index]');
        if (!item) return;
        const idx = parseInt(item.dataset.tierIndex, 10);
        const tier = this.tiers[idx];
        if (!tier) return;
        el.textContent = this.formatCartMoney(this.tierDisplayCents(tier));
      });
    }

    // Map current cents to a 0-100% fill where each tier sits at an evenly
    // spaced position on the bar (tier1 = 33%, tier2 = 66%, tier3 = 100%).
    // Within a segment, fill grows linearly with the customer's spend.
    computeFillPercent(currentCents, displayBreakpoints) {
      if (currentCents <= 0) return 0;
      if (currentCents >= displayBreakpoints[displayBreakpoints.length - 1]) return 100;
      const positions = [0];
      const total = displayBreakpoints.length;
      for (let i = 0; i < total; i++) {
        positions.push(((i + 1) / total) * 100);
      }
      const points = [0, ...displayBreakpoints];
      for (let i = 0; i < points.length - 1; i++) {
        if (currentCents >= points[i] && currentCents < points[i + 1]) {
          const segLen = points[i + 1] - points[i];
          const segProg = segLen > 0 ? (currentCents - points[i]) / segLen : 0;
          return positions[i] + segProg * (positions[i + 1] - positions[i]);
        }
      }
      return 0;
    }

    updateProgress(matrixCents) {
      if (!this.progressEl || !this.tiers.length) return;

      const currentCents = (this.cartTotalCents || 0) + (matrixCents || 0);
      const displayBreakpoints = this.tiers.map((t) => this.tierDisplayCents(t));
      const fillPct = this.computeFillPercent(currentCents, displayBreakpoints);

      if (this.progressFillEl) {
        this.progressFillEl.style.width = fillPct + '%';
      }

      // Find the highest achieved tier and the next one still to unlock.
      let nextTier = null;
      let nextIndex = -1;
      let topAchieved = null;
      this.tiers.forEach((t, i) => {
        if (currentCents >= this.tierDisplayCents(t)) {
          topAchieved = t;
        } else if (!nextTier) {
          nextTier = t;
          nextIndex = i;
        }
      });

      // Reflect achieved / next-target state on the dots and the legend labels.
      const applyState = (el) => {
        const idx = parseInt(el.dataset.tierIndex, 10);
        const tier = this.tiers[idx];
        if (!tier) return;
        const reached = currentCents >= this.tierDisplayCents(tier);
        el.classList.toggle('is-achieved', reached);
        el.classList.toggle('is-next', idx === nextIndex);
      };
      this.progressMarkerEls.forEach(applyState);
      this.querySelectorAll('.msm-progress__legend-item').forEach(applyState);

      if (!this.progressStatusEl) return;

      let html = '';
      if (nextTier) {
        const diff = this.tierDisplayCents(nextTier) - currentCents;
        html =
          'Spend <strong>' +
          this.formatCartMoney(diff) +
          '</strong> more to unlock <strong>' +
          this.escapeHtml(nextTier.label || nextTier.percent + '% savings') +
          '</strong>';
      } else if (topAchieved) {
        html =
          "You've unlocked <strong>" +
          this.escapeHtml(topAchieved.label || topAchieved.percent + '% savings') +
          '</strong> &mdash; the maximum savings';
      } else {
        html = 'Add items to unlock preferred pricing';
      }
      this.progressStatusEl.innerHTML = html;
    }

    escapeHtml(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    celebrate() {
      this.fireConfetti();
    }

    fireConfetti() {
      const host = this.confettiHostEl;
      if (!host) return;
      host.innerHTML = '';
      const colors = ['#1c8a3f', '#22a04a', '#3fc26b', '#16203A', '#DD612A', '#F4B400'];
      const pieces = 26;
      for (let i = 0; i < pieces; i++) {
        const piece = document.createElement('span');
        piece.className = 'msm-confetti__piece';
        const angle = Math.random() * Math.PI * 2;
        const distance = 70 + Math.random() * 90;
        const tx = Math.cos(angle) * distance;
        const ty = Math.sin(angle) * distance - 20;
        const rot = (Math.random() * 720 - 360).toFixed(0);
        const delay = (Math.random() * 80).toFixed(0);
        const dur = 900 + Math.round(Math.random() * 500);
        piece.style.setProperty('--tx', tx.toFixed(1) + 'px');
        piece.style.setProperty('--ty', ty.toFixed(1) + 'px');
        piece.style.setProperty('--rot', rot + 'deg');
        piece.style.animationDelay = delay + 'ms';
        piece.style.animationDuration = dur + 'ms';
        piece.style.background = colors[i % colors.length];
        host.appendChild(piece);
      }
      // Clean up after animation
      window.setTimeout(() => { if (host) host.innerHTML = ''; }, 1800);
    }

    updateBadge(topAchieved, isMaxed) {
      if (!this.progressBadgeEl) return;
      if (!topAchieved) {
        this.progressBadgeEl.textContent = 'Volume Deal';
        this.progressBadgeEl.dataset.state = 'idle';
        return;
      }
      this.progressBadgeEl.textContent = topAchieved.percent + '% OFF';
      this.progressBadgeEl.dataset.state = isMaxed ? 'max' : 'active';
    }


    formatMoney(cents) {
      if (typeof cents !== 'number' || isNaN(cents)) cents = 0;
      const format =
        (window.theme && window.theme.moneyFormat) ||
        (window.Shopify && window.Shopify.currency && '${{amount}}') ||
        '${{amount}}';
      return formatMoneyShopify(cents, format);
    }

    totalQty() {
      return this.inputs.reduce((sum, inp) => {
        if (inp.disabled) return sum;
        const v = parseInt(inp.value, 10);
        return sum + (isNaN(v) || v < 0 ? 0 : v);
      }, 0);
    }

    updateAddState() {
      if (!this.addBtn) return;
      const total = this.totalQty();
      const minQty = this.minOrderQty || 0;
      const empty = total === 0;
      const belowMin = minQty > 0 && total > 0 && total < minQty;
      // Keep the button clickable when below minimum so the submit handler
      // can scroll the user back to the size matrix. Only true 0-qty is hard-disabled.
      this.addBtn.disabled = empty;
      this.addBtn.setAttribute('aria-disabled', (empty || belowMin) ? 'true' : 'false');
      this.addBtn.classList.toggle('btn--disabled', empty || belowMin);
      this.addBtn.classList.toggle('is-below-min', belowMin);
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
      clearTimeout(this._errTimer);
      this._errTimer = setTimeout(() => {
        this.errorEl.hidden = true;
      }, 5000);
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    collectItems() {
      const items = [];
      this.inputs.forEach((inp) => {
        if (inp.disabled) return;
        const qty = parseInt(inp.value, 10);
        const id = parseInt(inp.dataset.variantId, 10);
        if (qty > 0 && id) items.push({ id: id, quantity: qty });
      });
      return items;
    }

    collectProperties() {
      const properties = {};
      const propInputs = this.propertyInputs(false);
      propInputs.forEach((inp) => {
        const m = inp.name.match(/properties\[(.+?)\]/);
        if (!m) return;
        const key = m[1];
        if (inp.type === 'checkbox' && !inp.checked) return;
        if (inp.type === 'radio' && !inp.checked) return;
        if (inp.value) properties[key] = inp.value;
      });
      return properties;
    }

    propertyInputs(includeFiles) {
      const selector = includeFiles
        ? 'input[name^="properties["], select[name^="properties["], textarea[name^="properties["]'
        : 'input[name^="properties["]:not([type="file"]), select[name^="properties["], textarea[name^="properties["]';
      const found = Array.from(this.form.querySelectorAll(selector));
      if (this.formId) {
        document.querySelectorAll(selector).forEach((input) => {
          if (input.getAttribute('form') === this.formId && found.indexOf(input) === -1) found.push(input);
        });
      }
      return found;
    }

    setLoading(loading) {
      if (!this.addBtn) return;
      if (loading) {
        this.addBtn.classList.add('btn--loading', 'is-loading');
        this.addBtn.setAttribute('aria-busy', 'true');
        this.addBtn.disabled = true;
      } else {
        this.addBtn.classList.remove('btn--loading', 'is-loading');
        this.addBtn.removeAttribute('aria-busy');
        this.updateAddState();
      }
    }

    onSubmit(e) {
      // Always handle submit ourselves when the matrix is active.
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const items = this.collectItems();
      if (!items.length) {
        this.showError('Please choose a quantity for at least one size.');
        return;
      }

      const minQty = this.minOrderQty || 0;
      const total = this.totalQty();
      if (minQty > 0 && total < minQty) {
        const remaining = minQty - total;
        this.showError('Minimum order is ' + minQty + ' ' + this.unitLabel + '. Add ' + remaining + ' more to continue.');
        this.scrollToMatrix();
        return;
      }

      this.addItems(items);
    }

    scrollToMatrix() {
      const target = this.querySelector('.multi-size-matrix__header') || this;
      if (!target) return;
      const headerEl = document.querySelector('[data-header-height]') || document.querySelector('.header');
      const headerOffset = headerEl ? headerEl.getBoundingClientRect().height : 80;
      const rect = target.getBoundingClientRect();
      const y = window.pageYOffset + rect.top - headerOffset - 16;
      window.scrollTo({ top: y, behavior: 'smooth' });

      // Flash the matrix briefly so the user notices
      this.classList.add('is-attention');
      window.setTimeout(() => this.classList.remove('is-attention'), 1200);

      // Focus the first non-zero or first available input
      const firstInput = Array.from(this.inputs).find((i) => !i.disabled);
      if (firstInput) {
        window.setTimeout(() => {
          try { firstInput.focus({ preventScroll: true }); } catch (e) { firstInput.focus(); }
          if (firstInput.value === '0') firstInput.select();
        }, 350);
      }
    }

    hasFileUpload() {
      const fileInputs = this.propertyInputs(true).filter((input) => input.type === 'file');
      return Array.from(fileInputs).some((f) => f.files && f.files.length > 0);
    }

    buildItemFormData(item) {
      const fd = new FormData();
      fd.append('id', String(item.id));
      fd.append('quantity', String(item.quantity));
      this.propertyInputs(true).forEach((inp) => {
          if (inp.type === 'file') {
            if (inp.files && inp.files[0]) fd.append(inp.name, inp.files[0]);
          } else if (inp.type === 'checkbox') {
            if (inp.checked) fd.append(inp.name, inp.value || 'on');
          } else if (inp.type === 'radio') {
            if (inp.checked) fd.append(inp.name, inp.value);
          } else if (inp.value) {
            fd.append(inp.name, inp.value);
          }
      });
      return fd;
    }

    async addItems(items) {
      this.setLoading(true);

      try {
        if (this.hasFileUpload()) {
          // Per-item POST with FormData so file uploads (e.g. logo) come through.
          for (const item of items) {
            const fd = this.buildItemFormData(item);
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
              body: fd,
            });
            if (!res.ok) {
              const data = await res.json().catch(() => ({}));
              const msg =
                data.description ||
                data.message ||
                'We could not add one of the items to the cart.';
              this.showError(msg);
              this.setLoading(false);
              return;
            }
          }
        } else {
          // Atomic bulk add via JSON.
          const properties = this.collectProperties();
          let payloadItems = items;
          if (Object.keys(properties).length) {
            payloadItems = items.map((it) =>
              Object.assign({}, it, { properties: properties })
            );
          }
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items: payloadItems }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            const msg =
              data.description ||
              data.message ||
              'We could not add these items to the cart.';
            this.showError(msg);
            this.setLoading(false);
            return;
          }
        }

        // Reset matrix inputs and refresh totals after successful add.
        this.inputs.forEach((inp) => {
          if (!inp.disabled) inp.value = '0';
        });
        this.updateTotals();
        this.updateAddState();

        // Notify theme to refresh cart UI, then open drawer or redirect.
        document.dispatchEvent(
          new CustomEvent('cart:refresh', { detail: { source: 'multi-size-matrix' } })
        );
        document.dispatchEvent(
          new CustomEvent('product:added', { detail: { source: 'multi-size-matrix' } })
        );

        const drawer = document.querySelector('cart-drawer, [data-cart-drawer]');
        if (drawer) {
          if (typeof drawer.open === 'function') drawer.open();
          else if (typeof drawer.show === 'function') drawer.show();
          else window.location.href = '/cart';
        } else {
          window.location.href = '/cart';
        }
      } catch (err) {
        console.error('[multi-size-matrix] add error', err);
        this.showError('Network error. Please try again.');
      } finally {
        this.setLoading(false);
      }
    }
  }

  if (window.customElements) {
    window.customElements.define('multi-size-matrix', MultiSizeMatrix);
  }
})();
